/**
 * LexisNexisConfigPage — Compliance & Capture · LexisNexis Config.
 *
 * Single-form panel surfacing the six `lexisnexis.*` SystemSettings rows
 * (master kill-switch, API base URL / product / environment, monthly budget
 * cap, call-log retention). The username / password rows were removed
 * 2026-10-08 (Bill): nothing read them - LexisNexis DDP authenticates with the
 * org id + API key held in Key Vault, never in SystemSettings. Defaults OFF — when off, remote
 * signing falls back to PCB's DIY KBA path. Pre-wired so admin can configure
 * credentials BEFORE a vendor contract signs; flipping `enabled` without
 * credentials still no-ops the endpoints (they return 503 LEXISNEXIS_DISABLED).
 *
 * Read:  GET /v3/config/system-settings/rows (filtered client-side to lexisnexis.*)
 *        + client.retail.lexisNexis.isEnabled() for the live server-gate probe
 * Write: client.retail.admin.systemSettings.upsert(SENTINEL, row) per changed
 *        key (the typed Wave-6.1 surface; sparse settingValue-only writes).
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer carries authority; no secret in
 * the bundle). No credential is ever entered here.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  type SystemSettingUpsertBody,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { TextField, SelectField, NumberField, BoolField } from '@/components/FormField';

interface RawSettingRow {
  settingKey: string;
  settingValue: string | null;
}

interface FormState {
  enabled: boolean;
  apiBaseUrl: string;
  apiProduct: string;
  apiEnvironment: string;
  budgetMonthlyMaxDollars: number | null;
  retentionCallLogMonths: number | null;
}

const KEYS = {
  enabled: 'lexisnexis.enabled',
  apiBaseUrl: 'lexisnexis.api.baseUrl',
  apiProduct: 'lexisnexis.api.product',
  apiEnvironment: 'lexisnexis.api.environment',
  budgetMonthlyMaxDollars: 'lexisnexis.budget.monthlyMaxDollars',
  retentionCallLogMonths: 'lexisnexis.retention.callLogMonths',
} as const;

const EMPTY: FormState = {
  enabled: false,
  apiBaseUrl: '',
  apiProduct: 'InstantID-Q&A-Auth',
  apiEnvironment: 'staging',
  budgetMonthlyMaxDollars: 1000,
  retentionCallLogMonths: 84,
};

const ENVIRONMENT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'staging', label: 'Staging (test)' },
  { value: 'production', label: 'Production (live customers)' },
];

export function LexisNexisConfigPage(): JSX.Element {
  const client = useApiClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [original, setOriginal] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [serverEnabled, setServerEnabled] = useState<boolean | null>(null);
  const [serverReason, setServerReason] = useState('');
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const probeServerEnabled = useCallback(async () => {
    try {
      const enabledRes = await client.retail.lexisNexis.isEnabled();
      setServerEnabled(enabledRes?.data?.enabled ?? null);
      setServerReason(enabledRes?.data?.reason ?? '');
    } catch {
      setServerEnabled(null);
      setServerReason('');
    }
  }, [client]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await client.http.get<{ success: boolean; data?: RawSettingRow[] }>(
          '/v3/config/system-settings/rows',
        );
        if (cancelled) return;
        const all = res?.data ?? [];
        const next: FormState = { ...EMPTY };
        for (const r of all) {
          switch (r.settingKey) {
            case KEYS.enabled:
              next.enabled = (r.settingValue ?? 'false').toLowerCase() === 'true';
              break;
            case KEYS.apiBaseUrl:
              next.apiBaseUrl = r.settingValue ?? '';
              break;
            case KEYS.apiProduct:
              next.apiProduct = r.settingValue ?? 'InstantID-Q&A-Auth';
              break;
            case KEYS.apiEnvironment:
              next.apiEnvironment = r.settingValue ?? 'staging';
              break;
            case KEYS.budgetMonthlyMaxDollars:
              next.budgetMonthlyMaxDollars = r.settingValue ? Number(r.settingValue) : 1000;
              break;
            case KEYS.retentionCallLogMonths:
              next.retentionCallLogMonths = r.settingValue ? Number(r.settingValue) : 84;
              break;
          }
        }
        setForm(next);
        setOriginal(next);
        await probeServerEnabled();
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, probeServerEnabled, refreshNonce]);

  const isDirty = useMemo(
    () =>
      form.enabled !== original.enabled ||
      form.apiBaseUrl !== original.apiBaseUrl ||
      form.apiProduct !== original.apiProduct ||
      form.apiEnvironment !== original.apiEnvironment ||
      form.budgetMonthlyMaxDollars !== original.budgetMonthlyMaxDollars ||
      form.retentionCallLogMonths !== original.retentionCallLogMonths,
    [form, original],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const rows: SystemSettingUpsertBody[] = [];
      if (form.enabled !== original.enabled) {
        rows.push({
          settingKey: KEYS.enabled,
          settingValue: form.enabled ? 'true' : 'false',
          dataType: 'bool',
          categoryCode: 'lexisnexis',
          isActive: true,
        });
      }
      if (form.apiBaseUrl !== original.apiBaseUrl) {
        rows.push({
          settingKey: KEYS.apiBaseUrl,
          settingValue: form.apiBaseUrl,
          dataType: 'url',
          categoryCode: 'lexisnexis',
          isActive: true,
        });
      }
      if (form.apiProduct !== original.apiProduct) {
        rows.push({
          settingKey: KEYS.apiProduct,
          settingValue: form.apiProduct,
          dataType: 'string',
          categoryCode: 'lexisnexis',
          isActive: true,
        });
      }
      if (form.apiEnvironment !== original.apiEnvironment) {
        rows.push({
          settingKey: KEYS.apiEnvironment,
          settingValue: form.apiEnvironment,
          dataType: 'string',
          categoryCode: 'lexisnexis',
          isActive: true,
        });
      }
      if (form.budgetMonthlyMaxDollars !== original.budgetMonthlyMaxDollars) {
        rows.push({
          settingKey: KEYS.budgetMonthlyMaxDollars,
          settingValue: String(form.budgetMonthlyMaxDollars ?? 1000),
          dataType: 'decimal',
          categoryCode: 'lexisnexis',
          isActive: true,
        });
      }
      if (form.retentionCallLogMonths !== original.retentionCallLogMonths) {
        rows.push({
          settingKey: KEYS.retentionCallLogMonths,
          settingValue: String(form.retentionCallLogMonths ?? 84),
          dataType: 'int',
          categoryCode: 'lexisnexis',
          isActive: true,
        });
      }

      for (const row of rows) {
        const resp = await client.retail.admin.systemSettings.upsert(ADMIN_AUTH_TOKEN_SENTINEL, row);
        if (!resp.success) throw new Error(resp.error ?? `upsert failed for ${row.settingKey}`);
      }
      setOriginal(form);
      setToast({ tone: 'info', text: `Saved ${rows.length} setting${rows.length === 1 ? '' : 's'}.` });
      await probeServerEnabled();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [client, form, original, probeServerEnabled]);

  const handleTestConnection = useCallback(async () => {
    try {
      const enabledRes = await client.retail.lexisNexis.isEnabled();
      if (enabledRes?.data?.enabled) {
        setToast({
          tone: 'info',
          text: 'Integration is enabled server-side. (Live wire-call test deferred until vendor contract.)',
        });
      } else {
        setToast({
          tone: 'warn',
          text: `Integration disabled: ${enabledRes?.data?.reason ?? '(reason unknown)'}`,
        });
      }
      setServerEnabled(enabledRes?.data?.enabled ?? null);
      setServerReason(enabledRes?.data?.reason ?? '');
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load LexisNexis config: {loadError}{' '}
        <Button onClick={() => setRefreshNonce((n) => n + 1)}>Retry</Button>
      </Banner>
    );
  }
  if (loading) return <Spinner label="Loading LexisNexis config…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="warn">
        <strong>Heads up.</strong> PCB has no LexisNexis contract today. Enabling this without a
        contract has no effect — the KBA endpoints still return 503 (the server checks credentials
        too). When the contract is signed, fill in the URL below, set the master switch to ON, and
        the integration goes live with no code change. Credentials live in Key Vault, never here. Cost: pay-per-call (~$2–4 per KBA
        challenge).
      </Banner>

      <Card
        title="Master switch"
        actions={
          serverEnabled === null ? undefined : (
            <Pill tone={serverEnabled ? 'ok' : 'muted'}>
              Server says: {serverEnabled ? 'enabled' : 'disabled'}
            </Pill>
          )
        }
      >
        <div className="form-stack">
          <BoolField
            label="Enable LexisNexis identity verification"
            value={form.enabled}
            onChange={(v) => setForm({ ...form, enabled: v })}
            hint="When OFF (default), every /v3/lexisnexis/* endpoint returns HTTP 503 (LEXISNEXIS_DISABLED) and remote signing uses PCB's DIY KBA instead."
          />
          {serverReason && (
            <p className="cell-desc">
              <em>Server reason:</em> {serverReason}
            </p>
          )}
        </div>
      </Card>

      <Card title="API endpoint">
        <div className="form-stack">
          <SelectField
            label="Environment"
            value={form.apiEnvironment}
            onChange={(v) => setForm({ ...form, apiEnvironment: v })}
            options={ENVIRONMENT_OPTIONS}
            hint="Picks production or staging endpoints. Use staging while testing — vendor charges differ."
          />
          <TextField
            label="API base URL"
            value={form.apiBaseUrl}
            onChange={(v) => setForm({ ...form, apiBaseUrl: v })}
            placeholder="https://wsonline.lexisnexis.com/..."
            monospace
            hint="Full URL of the LexisNexis InstantID Q&A endpoint. Vendor supplies the exact value once the contract is signed."
          />
          <TextField
            label="Product name"
            value={form.apiProduct}
            onChange={(v) => setForm({ ...form, apiProduct: v })}
            placeholder="InstantID-Q&A-Auth"
            hint="LexisNexis product identifier. Default is InstantID-Q&A-Auth (KBA challenge)."
          />
        </div>
      </Card>

      <Card title="Budget & retention">
        <div className="form-stack">
          <NumberField
            label="Monthly budget cap (USD)"
            value={form.budgetMonthlyMaxDollars}
            onChange={(v) => setForm({ ...form, budgetMonthlyMaxDollars: v })}
            min={0}
            step={100}
            hint="Soft cap — once cumulative vendor cost in the current calendar month exceeds this amount, the service logs a warning."
          />
          <NumberField
            label="Call-log retention (months)"
            value={form.retentionCallLogMonths}
            onChange={(v) => setForm({ ...form, retentionCallLogMonths: v })}
            min={1}
            max={240}
            hint="How long to keep KBA call audit rows. Default 84 = 7 years, matching the CIP-extended retention rule for identity-verification artefacts."
          />
        </div>
      </Card>

      {saveError && <Banner tone="err">{saveError}</Banner>}

      <Card title="">
        <div className="card-actions-row">
          <Button variant="primary" onClick={() => void handleSave()} disabled={saving || !isDirty}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
          <Button onClick={() => void handleTestConnection()} disabled={saving}>
            Test connection
          </Button>
          {isDirty && <span className="cell-desc">Unsaved changes.</span>}
        </div>
      </Card>
    </>
  );
}
